import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { getMeetingActions, getAction, getActions, deleteAction, getActionsAcl, getPinnedActions } from '../api';
import ActionForm from '../form/ActionForm';
import { useHistory } from 'react-router-dom';
import CommentsList from '../../Comment/list/CommentsList';
import ActionCard from '../card/ActionCard';
import ListHeader from '../../../common/ui/components/ListHeader/ListHeader';
import DeleteConfirmation from '../../../common/ui/components/DeleteConfirmation/DeleteConfirmation';
import Pagination from '../../../common/ui/components/Pagination/Pagination';
import { authorizeUser } from '../../Login/api';
import '../styles.scss';

const ActionList = forwardRef((props, ref) => {
    const { meetingId, openForm, showFormOptions, match } = props;

    const [actions, setActions] = useState([]);
    const [actionsCount, setActionsCount] = useState(0);
    const [pinnedActions, setPinnedActions] = useState([]);
    const [pinnedActionsCount, setPinnedActionsCount] = useState(0);
    const [defaultPage, setDefaultPage] = useState(1);
    const [shouldShowForm, setShouldShowForm] = useState(false);
    const [actionToEdit, setActionToEdit] = useState(null);
    const [actionsAcl, setActionsAcl] = useState({});
    const [focusedActionId, setFocusedActionId] = useState(match ? match.params.actionId : null);
    const [commentMentionedList, setCommentMentionedList] = useState([]);
    const [actionIdToDelete, setActionIdToDelete] = useState(null);
    const [isDeleteConfirmationShowing, setIsDeleteConfirmationShowing] = useState(false);
    const [isPinnedActionsToggled, setIsPinnedActionsToggled] = useState(false);
    const [logedInUser, setLogedInUser] = useState({});

    const history = useHistory();

    useEffect(() => {
        const fetchLogedInUser = async () => {
            setLogedInUser(await authorizeUser());
        };

        fetchLogedInUser();
    }, []);
    useEffect(() => {
        setFocusedActionId(match ? match.params.actionId : null);
    }, [history.location.pathname]);
    useEffect(() => {
        const fetchPinnedActions = async () => {
            const pinnedActions = await getPinnedActions();
            const { actions, count } = pinnedActions;
            if (!count || !actions) return;
            setPinnedActions(actions);
            setPinnedActionsCount(count);
        };

        fetchPinnedActions();
    }, []);
    useEffect(() => {
        handlePageChange(1);
        const fetchActionsAcl = async () => {
            if (!meetingId) return;
            setActionsAcl(await getActionsAcl(meetingId));
        };

        fetchActionsAcl();
        if (openForm) handleToggleForm();
    }, [meetingId]);
    useEffect(() => {
        const fetchFocusedAction = async () => {
            if (!focusedActionId) return;
            const fetchedAction = await getAction(focusedActionId);
            if (!fetchedAction) return;
            handlePageChange(fetchedAction.pageNumber);
            setCommentMentionedList(fetchedAction ? fetchedAction.participants : []);
        };
        fetchFocusedAction();
    }, [focusedActionId]);

    useImperativeHandle(ref, () => ({
        handlePublishSummaryFormAction() {
            setActionsAcl({ create: false, edit: false, delete: false });
        },
        handleRevertSummaryFormAction() {
            setActionsAcl({ create: true, edit: true, delete: true });
        },
    }));

    const handlePageChange = pageNumber => {
        setDefaultPage(null);
        setDefaultPage(pageNumber);

        const fetchActions = async () => {
            const data = meetingId ? await getMeetingActions(meetingId) : await getActions(pageNumber);
            setActionsCount(meetingId ? 0 : data.count);
            setActions(meetingId ? data : data.actions);
        };
        fetchActions();
    };
    const handleToggleForm = () => {
        setShouldShowForm(!shouldShowForm);
    };
    const handleAddAction = action => {
        setShouldShowForm(false);
        setActions(actions => [action, ...actions]);
    };
    const handleUpdateAction = updatedAction => {
        setShouldShowForm(false);
        const indexOfUpdatedAction = actions.findIndex(action => action._id === updatedAction._id);
        const updatedActions = [...actions];
        updatedActions[indexOfUpdatedAction] = updatedAction;
        setActions(updatedActions);
        setActionToEdit(null);
    };
    const handleEditAction = action => {
        setActionToEdit(action);
        setShouldShowForm(true);
    };
    const handleDeleteAction = async () => {
        if (!actionIdToDelete) return;
        await deleteAction(meetingId, actionIdToDelete);
        const remainingActions = actions.filter(action => {
            return action._id !== actionIdToDelete;
        });

        setActions(remainingActions);
        setIsDeleteConfirmationShowing(false);
    };
    const handleToggleComments = actionId => {
        if (focusedActionId === actionId || !actionId) {
            history.push(meetingId ? '../' : '../actions');
            return;
        }
        setFocusedActionId(actionId);
        history.push(meetingId ? `/meetings/${meetingId}/actions/${actionId}` : `/actions/${actionId}`);
    };
    const handleTogglePinnedActions = () => {
        const shouldTogglePinnedActionsList = pinnedActionsCount > 0 ? !isPinnedActionsToggled : false;
        setIsPinnedActionsToggled(shouldTogglePinnedActionsList);
        if (!shouldTogglePinnedActionsList) handlePageChange(1);
    };

    const renderListData = (
        <>
            {(isPinnedActionsToggled ? pinnedActions : actions).length ? (
                <>
                    {[...(isPinnedActionsToggled ? pinnedActions : actions)].map((action, i) => (
                        <ActionCard
                            isMeetingProfileToggled={Boolean(meetingId)}
                            logedInUser={logedInUser}
                            pinnedActions={pinnedActions}
                            setPinnedActions={setPinnedActions}
                            setPinnedActionsCount={setPinnedActionsCount}
                            pinnedActionsCount={pinnedActionsCount}
                            key={`actions-${i}`}
                            action={action}
                            focusedActionId={focusedActionId}
                            handleToggleComments={handleToggleComments}
                            handleEditAction={handleEditAction}
                            handleDeleteAction={() => {
                                setActionIdToDelete(action._id);
                                setIsDeleteConfirmationShowing(true);
                            }}
                            acl={meetingId ? actionsAcl : null}
                        />
                    ))}
                </>
            ) : (
                'اطلاعاتی وجود ندارد'
            )}
        </>
    );

    const mainActionsList = (
        <>
            {!meetingId && (
                <div className="pinned-actions-tag" onClick={handleTogglePinnedActions}>
                    {pinnedActionsCount > 0 ? `${pinnedActionsCount} عدد اقدام پین شده` : 'اقدامی پین نشده است'}
                </div>
            )}
            {renderListData}
            {!meetingId && defaultPage && (
                <Pagination
                    defaultPage={defaultPage}
                    count={actionsCount}
                    onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                />
            )}
        </>
    );

    const pinnedActionsList = (
        <>
            <div className="pinned-actions-tag" onClick={handleTogglePinnedActions}>
                مشاهده لیست همه اقدامات
            </div>
            {renderListData}
        </>
    );

    return (
        <div className="comments-sidebar-layout list-layout">
            {match && match.params.actionId && (
                <div onClick={() => handleToggleComments()} className="unfocused-background" />
            )}

            <div className={`content ${focusedActionId ? 'focused' : ''}`}>
                <div className={`list ${focusedActionId ? 'remove-list-margin' : ''}`}>
                    <div className="summary">
                        {shouldShowForm ? (
                            <ActionForm
                                actionToEdit={actionToEdit}
                                handleCancel={() => {
                                    handleToggleForm();
                                    setActionToEdit(null);
                                }}
                                handleAddAction={handleAddAction}
                                handleUpdateAction={handleUpdateAction}
                                meetingId={meetingId}
                            />
                        ) : (
                            <>
                                <ListHeader
                                    listTitle="اقدامات"
                                    handleOpenForm={showFormOptions ? handleToggleForm : null}
                                />
                                {isPinnedActionsToggled ? pinnedActionsList : mainActionsList}

                                <DeleteConfirmation
                                    onClose={() => {
                                        setIsDeleteConfirmationShowing(false);
                                    }}
                                    onClickOk={() => {
                                        handleDeleteAction();
                                    }}
                                    isOpen={isDeleteConfirmationShowing}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            {focusedActionId && (
                <div className="comments-sidebar">
                    <CommentsList mentionedList={commentMentionedList} />
                </div>
            )}
        </div>
    );
});
export default ActionList;
